// Pure, unit-testable helpers for the PNR operation-center dialog
// (`roster-pnr-drawer.tsx` redesign). These were lifted VERBATIM out of the old
// monolithic drawer so the presentational components can import them without
// dragging React/data deps along, PLUS a state synthesizer:
//
//  - `synthesizeStatus()` collapses the live rebooking/ticket/hotel/voucher state
//    into the ONE coherent status chip + a `flight · hotel · ticket` status line
//    the hero ribbon renders (the single-status-chip rule from §18).
//
// Friendly labels are primary; the synthesizers reuse `rebookingStatusLabel` /
// `REBOOKING_STATUS_TONE` from `labels.ts` for the non-special statuses.
//
// i18n: like `labels.ts`, this module holds translation KEYS, never language.
// Each synthesizer takes the translator as its LAST, optional argument — a
// component passes the `t` from `useTranslation(…)`; every other caller falls
// back to `disruptionsT`, which resolves against the console's live language.

import type { BadgeVariant } from '@/components/ui/badge';
import type { OptionSegment } from '@/lib/rebooking/types';
import type { DisruptionPnrGroupRow, RosterRebookingStatus } from '@/lib/disruptions/types';
import type { PurchasePhase } from '@/lib/recovery/recovery-model';
import {
  disruptionsT,
  noOptionsReasonLabel,
  rebookingStatusLabel,
  REBOOKING_STATUS_TONE,
  type DisruptionsT,
} from './labels';

/** Human duration from milliseconds — "6h 20m" / "55m". */
export function fmtDuration(ms: number): string {
  const mins = Math.max(0, Math.round(ms / 60_000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h${m ? ` ${m}m` : ''}` : `${m}m`;
}

/** Summarise an option's itinerary: the actual `from→to`, the number of stops +
 *  via-codes, total elapsed, and whether any leg is interline. Makes a 2-leg
 *  connecting option legible instead of a flat list of flights. */
export function journeyOf(segments: OptionSegment[]): {
  from: string;
  to: string;
  stops: number;
  via: string[];
  totalLabel: string;
  anyInterline: boolean;
} {
  const first = segments[0];
  const last = segments[segments.length - 1];
  const totalMs =
    first && last ? new Date(last.arrival).getTime() - new Date(first.departure).getTime() : NaN;
  return {
    from: first?.origin ?? '?',
    to: last?.destination ?? '?',
    stops: Math.max(0, segments.length - 1),
    via: segments.slice(0, -1).map((s) => s.destination),
    totalLabel: Number.isFinite(totalMs) && totalMs > 0 ? fmtDuration(totalMs) : '',
    anyInterline: segments.some((s) => s.isInterline),
  };
}

/** Layover between a segment's arrival and the next segment's departure. */
export function layoverLabel(a: OptionSegment, b: OptionSegment): string {
  const ms = new Date(b.departure).getTime() - new Date(a.arrival).getTime();
  return Number.isFinite(ms) && ms > 0 ? fmtDuration(ms) : '—';
}

/** "LA456 GRU→SCL 10:40" for the assigned flight; degrades on missing parts.
 *  NB: the time portion is intentionally left to the caller (it needs the
 *  airport TZ); this returns the route-only `flightNumber route` skeleton so it
 *  stays pure + TZ-free. The drawer composes the time alongside. */
export function assignedFlightText(af: DisruptionPnrGroupRow['assignedFlight']): string {
  if (!af) return '—';
  const route =
    af.origin && af.destination ? `${af.origin}→${af.destination}` : (af.destination ?? '');
  return [af.number, route].filter(Boolean).join(' ') || '—';
}

/**
 * Local calendar-day index (days since epoch) of a flight time. A full ISO
 * instant uses its LOCAL date (browser TZ ≈ the operating airport's). A date-only
 * `YYYY-MM-DD` (some ingestion paths denormalise the disrupted flight's scheduled
 * date without a time) is read as a LOCAL calendar date — NOT `new Date('…')`,
 * which would parse it as UTC midnight and roll a UTC-behind operator back a day
 * (the very skew that would mis-flag a same-day reflight as overnight).
 */
export function localDayIndex(iso: string): number | undefined {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (dateOnly) {
    const y = Number(dateOnly[1]);
    const mo = Number(dateOnly[2]) - 1;
    const day = Number(dateOnly[3]);
    return Math.floor(new Date(y, mo, day).getTime() / 86_400_000);
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  return Math.floor(new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() / 86_400_000);
}

/**
 * Does the re-accommodation imply an overnight stay? Mirrors the backend
 * `deriveHotelRequirement` (rebooking.tds §12): a hotel is needed only when the
 * assigned flight departs on a LATER local calendar day than the disrupted
 * flight — a same-day reflight needs none. Returns `undefined` when either time
 * is missing/unparseable so the caller can show a neutral state rather than
 * guessing. Provisional only — the authoritative `requiresHotel` reconciles from
 * cases asynchronously.
 */
export function impliesOvernight(disruptedIso?: string, assignedIso?: string): boolean | undefined {
  if (!disruptedIso || !assignedIso) return undefined;
  const ref = localDayIndex(disruptedIso);
  const assigned = localDayIndex(assignedIso);
  if (ref === undefined || assigned === undefined) return undefined;
  return assigned > ref;
}

/** Recovery delay (original → re-accommodated departure); `warn` when it pushes
 *  more than a day out (outside the disruption recovery window). */
export function recoveryDelay(
  originalIso?: string,
  newIso?: string,
  t: DisruptionsT = disruptionsT,
): { label: string; warn: boolean } | null {
  if (!originalIso || !newIso) return null;
  const ms = new Date(newIso).getTime() - new Date(originalIso).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const hours = ms / 3_600_000;
  const warn = hours >= 24;
  let label: string;
  if (hours < 1) label = `+${Math.round(ms / 60_000)}m`;
  else if (hours < 24) label = `+${Math.round(hours)}h`;
  else {
    label = t('disruptions:pnrStatus.recoveryDays', { count: Math.round(hours / 24) });
  }
  return { label, warn };
}

/* ── synthesizeStatus ─────────────────────────────────────────────────────────
 * Collapse the LIVE rebooking/ticket/hotel/voucher state into ONE coherent status
 * chip (the §18 single-status-chip rule) + a `flight · hotel/voucher · ticket`
 * status line for the hero ribbon. Precedence (highest first):
 *   NOT_APPLICABLE → ticketed (success) → selected/SELECTED ("Re-accommodated",
 *   success) → OVERRIDDEN → NO_OPTIONS (error) → OPTIONS_READY (info) → DEFERRED →
 *   PENDING ("Awaiting flight", warning).
 * The recovery-window ⚠ (when the re-accommodation lands outside the window) is
 * appended as the last status-line segment, never as the headline. */

/** The kind of accommodation the rebooking engine decided for the PNR. */
type AccommodationKind = 'HOTEL' | 'MEAL_VOUCHER' | 'NONE';

export interface StatusSynthesis {
  /** The ONE coherent status chip — friendly label + Badge tone. */
  headline: { label: string; tone: BadgeVariant };
  /** The `·`-joined status line (hotel/voucher · ticket-pending · ⚠ window) —
   *  complements the headline chip, never repeats it. */
  line: string;
  /** The individual status-line segments (pre-join), for callers that want chips. */
  segments: string[];
}

export interface SynthesizeStatusInput {
  /** The LIVE rebooking status (options-query first, group denorm fallback). */
  liveStatus: RosterRebookingStatus;
  /** A re-accommodation option IS chosen (live `selectedOptionUrn` or SELECTED). */
  isSelected: boolean;
  /** The PSS ticket is issued. Wins the headline outright. */
  isTicketed: boolean;
  /** Live hotel reservation status, when a reservation exists for this PNR. */
  hotelReservationStatus?: string | undefined;
  /** Authoritative overnight need (cases denorm) — present once reconciled. */
  requiresHotel?: boolean | undefined;
  /** Provisional overnight need derived from the chosen flight (pre-denorm). */
  provisionalOvernight?: boolean | undefined;
  /** Meal-voucher entitlement (present when accommodationKind === MEAL_VOUCHER). */
  mealVoucher?: { amountUsd: number; issued?: boolean } | undefined;
  /** The accommodation outcome decided by rebooking. */
  accommodationKind?: AccommodationKind | undefined;
  /** The re-accommodation lands outside the recovery window (≥24h). */
  recoveryWarn?: boolean | undefined;
  /** Coded reason the group is NO_OPTIONS / NOT_APPLICABLE — when `liveStatus` is
   *  NO_OPTIONS the human copy replaces the bare "No options" headline + line. */
  noOptionsReason?: string | undefined;
  /** The CASE is care-only (`rebookingDisabled`): flight/hotel/meals/transport are
   *  OFFERINGS the operator MAY arrange, never obligations — the passenger might
   *  already hold a flight booked elsewhere. Presentation only: the headline never
   *  warns "Awaiting flight", NOT_APPLICABLE reads as the calm "Own arrangements"
   *  (the POLICY_DISABLED reason copy would be a lie here), and the hotel-needed
   *  segment softens to "Hotel optional". The wire (`requiresHotel` etc.) is
   *  untouched. */
  careOnly?: boolean | undefined;
  /** #771 — a CONFIRMED replacement purchase on the external (LCC) rail. Money
   *  moved at the vendor: it outranks every "waiting" headline (PENDING,
   *  OPTIONS_READY, NO_OPTIONS, DEFERRED, care-only), but never a legacy-rail
   *  ticket/selection, which remain explicit internal assignments. */
  externalBooking?:
    | { provider: string; bookingReference?: string | undefined; mode?: string | undefined }
    | undefined;
  /** Where Nexa's purchase of the chosen flight is (refused, recovered, verified,
   *  running). Replaces «ticket pending» — a refused purchase is never pending. */
  purchase?: PurchasePhase | null | undefined;
}

export function synthesizeStatus(
  input: SynthesizeStatusInput,
  t: DisruptionsT = disruptionsT,
): StatusSynthesis {
  const {
    liveStatus,
    isSelected,
    isTicketed,
    hotelReservationStatus,
    requiresHotel,
    provisionalOvernight,
    mealVoucher,
    accommodationKind,
    recoveryWarn,
    noOptionsReason,
    careOnly,
    externalBooking,
    purchase,
  } = input;

  // The human "why" for a terminal no-options verdict (POLICY_DISABLED, NO_INVENTORY,
  // …) — surfaced instead of the bare "No options" when a recognised code is present.
  const reasonCopy = noOptionsReasonLabel(noOptionsReason, t);

  // Headline — single coherent chip, precedence-ordered.
  let headline: { label: string; tone: BadgeVariant };
  if (liveStatus === 'NOT_APPLICABLE') {
    // On a care-only case NOT_APPLICABLE is the DECLARED state, not a policy
    // verdict — "Rebooking not enabled by policy" (the POLICY_DISABLED stamp's
    // copy) would be a lie. Say the calm truth instead.
    headline = careOnly
      ? { label: t('disruptions:pnrStatus.ownArrangements'), tone: 'muted' }
      : {
          label: rebookingStatusLabel('NOT_APPLICABLE', t),
          tone: REBOOKING_STATUS_TONE.NOT_APPLICABLE,
        };
  } else if (isTicketed) {
    headline = { label: t('disruptions:pnrStatus.ticketed'), tone: 'success' };
  } else if (isSelected || liveStatus === 'SELECTED') {
    // "Re-accommodated" reads truer than the engine's "Selected"/"Flight assigned"
    // once a flight is chosen but not yet ticketed.
    headline = { label: t('disruptions:pnrStatus.reaccommodated'), tone: 'success' };
  } else if (liveStatus === 'OVERRIDDEN') {
    headline = {
      label: rebookingStatusLabel('OVERRIDDEN', t),
      tone: REBOOKING_STATUS_TONE.OVERRIDDEN,
    };
  } else if (externalBooking) {
    // #771 — the group IS resolved, on the external rail: say so instead of any
    // "waiting"/"no options" headline that would push the operator to double work.
    headline = { label: t('disruptions:pnrStatus.externalPurchased'), tone: 'success' };
  } else if (liveStatus === 'NO_OPTIONS') {
    // Surface the human reason ("Rebooking not enabled by policy.", …) over the bare
    // "No options" when a recognised code is present.
    headline = {
      label: reasonCopy ?? rebookingStatusLabel('NO_OPTIONS', t),
      tone: REBOOKING_STATUS_TONE.NO_OPTIONS,
    };
  } else if (liveStatus === 'OPTIONS_READY') {
    headline = {
      label: rebookingStatusLabel('OPTIONS_READY', t),
      tone: REBOOKING_STATUS_TONE.OPTIONS_READY,
    };
  } else if (liveStatus === 'DEFERRED') {
    headline = {
      label: rebookingStatusLabel('DEFERRED', t),
      tone: REBOOKING_STATUS_TONE.DEFERRED,
    };
  } else if (careOnly) {
    // Care-only + no flight chosen: never the "Awaiting flight" warning — a
    // flight is an offering here, not something the PNR is waiting on.
    headline = { label: t('disruptions:pnrStatus.ownArrangements'), tone: 'muted' };
  } else {
    // PENDING (and any unforeseen status) — "Awaiting flight".
    headline = { label: t('disruptions:pnrStatus.awaitingFlight'), tone: 'warning' };
  }

  // Status line — hotel/voucher · ticket · ⚠ window. Each segment is added only
  // when there's something true to say AND the headline chip doesn't already say
  // it: "Ticketed" / "Re-accommodated" / "Options ready" / "No options" all imply
  // the flight state, so the line never echoes the chip (the duplicated-status
  // complaint from the card redesign).
  const segments: string[] = [];

  // Hotel / voucher segment — authoritative reservation status first, then the
  // accommodation decision, then the provisional overnight need.
  const needsHotel = requiresHotel === true || provisionalOvernight === true;
  if (hotelReservationStatus === 'BOOKED') {
    segments.push(t('disruptions:pnrStatus.hotelBooked'));
  } else if (hotelReservationStatus === 'HELD') {
    // The reversible offer's room — same word as the roster chip ("Hotel held").
    segments.push(t('disruptions:pnrStatus.hotelHeld'));
  } else if (hotelReservationStatus === 'PENDING') {
    segments.push(t('disruptions:pnrStatus.hotelPending'));
  } else if (accommodationKind === 'MEAL_VOUCHER' || mealVoucher) {
    segments.push(
      mealVoucher?.issued
        ? t('disruptions:pnrStatus.voucherIssued')
        : t('disruptions:pnrStatus.mealVoucher'),
    );
  } else if (accommodationKind === 'HOTEL' || needsHotel) {
    // Care-only: the stay is an offering, not a demand — "needed" would imply an
    // obligation the case does not carry. Same wire facts, calmer word.
    segments.push(
      careOnly ? t('disruptions:pnrStatus.hotelOptional') : t('disruptions:pnrStatus.hotelNeeded'),
    );
  } else if (accommodationKind === 'NONE' || requiresHotel === false || provisionalOvernight === false) {
    segments.push(t('disruptions:pnrStatus.noHotel'));
  }

  // Ticket segment — pending while selected-but-not-ticketed. Once ticketed the
  // HEADLINE says so; the line doesn't repeat it.
  if (!isTicketed && purchase) {
    segments.push(t(`pnr:steps.flight.state.purchase.${purchase}`));
  } else if (!isTicketed && (isSelected || liveStatus === 'SELECTED')) {
    segments.push(t('disruptions:pnrStatus.ticketPending'));
  }

  // External-purchase reference — only when the external headline is showing
  // (never when a ticket/selection outranked it), so the hero carries the
  // locator without echoing the chip.
  if (externalBooking?.bookingReference && !isTicketed && !isSelected && liveStatus !== 'SELECTED') {
    segments.push(
      t('disruptions:pnrStatus.externalRef', { reference: externalBooking.bookingReference }),
    );
  }

  // Recovery-window ⚠ — always last.
  if (recoveryWarn) {
    segments.push(t('disruptions:pnrStatus.outsideWindow'));
  }

  return { headline, line: segments.join(' · '), segments };
}
